export type Locale = "en" | "ru";

export interface CaseLink {
  label: string;
  href: string;
}

export interface CaseItem {
  heading: string;
  sub: string;
  body: string;
  links?: CaseLink[];
}

export interface ContactLink {
  label: string;
  href: string;
}

export interface Dict {
  htmlLang: string;
  meta: { title: string; description: string };
  ui: {
    themeToggleLight: string;
    themeToggleDark: string;
    langToggle: string;
    langSwitchLabel: string;
    skipToContent: string;
    menu: string;
    close: string;
  };
  nav: { about: string; process: string; build: string; cases: string; contacts: string };
  hero: { eyebrow: string; name: string; tagline: string };
  about: {
    title: string;
    stack: { heading: string; items: string[] };
    experience: { heading: string; body: string };
    directions: { heading: string; items: string[] };
  };
  process: {
    title: string;
    approach: { heading: string; steps: string[] };
    ai: { heading: string; items: string[] };
  };
  build: {
    title: string;
    intro: string;
    items: { heading: string; body: string }[];
  };
  cases: { title: string; items: CaseItem[] };
  contacts: {
    title: string;
    links: ContactLink[];
    form: {
      title: string;
      sub: string;
      labels: { name: string; phone: string; email: string; comment: string };
      send: string;
      sending: string;
      successTitle: string;
      successBody: string;
      summaryPrefix: string;
      errorTitle: string;
      errorFallback: string;
    };
  };
  validation: {
    required: string;
    nameMin: string;
    phoneInvalid: string;
    phoneExpectDigits: string;
    phoneWrongCode: string;
    emailMissingAt: string;
    emailMissingDomain: string;
    emailInvalid: string;
    commentMin: string;
  };
  footer: {
    text: string;
    sourceLabel: string;
    repoHref: string;
  };
  chat: {
    open: string;
    close: string;
    title: string;
    sub: string;
    greeting: string;
    placeholder: string;
    send: string;
    thinking: string;
    toolsUsedPrefix: string;
    toolNote: string;
    examples: string[];
    hint: string;
  };
}

const en: Dict = {
  htmlLang: "en",
  meta: {
    title: "Selim Ataballyev - Frontend / Fullstack Engineer",
    description: "Mid-level frontend / fullstack engineer. TypeScript, Nuxt, Next, Node, AI integrations.",
  },
  ui: {
    themeToggleLight: "Switch to light",
    themeToggleDark: "Switch to dark",
    langToggle: "RU",
    langSwitchLabel: "Switch to Russian",
    skipToContent: "Skip to content",
    menu: "Open menu",
    close: "Close",
  },
  nav: { about: "About", process: "Process", build: "Build", cases: "Cases", contacts: "Contacts" },
  hero: {
    eyebrow: "Frontend / Fullstack Engineer",
    name: "Selim Ataballyev",
    tagline: "Building since 14, commercial since 2020. TypeScript-first. Frontend, Node backends, AI integrations.",
  },
  about: {
    title: "About",
    stack: {
      heading: "Stack",
      items: ["TypeScript", "Nuxt 3", "Next.js", "Vue 3", "React", "Node.js", "Hono", "NestJS", "SCSS / Tailwind", "PostgreSQL", "Docker", "Strapi / WordPress"],
    },
    experience: {
      heading: "Experience",
      body: "6 years commercial. Production web apps, browser extensions, CRMs, AI integrations, video pipelines. Mentored 25+ devs across teams.",
    },
    directions: {
      heading: "Directions",
      items: [
        "Frontend SPAs and landing pages",
        "SSR / static (Nuxt, Next, Astro)",
        "Node APIs (Hono, Nest, Express)",
        "Browser extensions (MV3, multi-browser)",
        "AI integrations (OpenAI, Anthropic, tool calling)",
      ],
    },
  },
  process: {
    title: "How I work",
    approach: {
      heading: "Approach to tasks",
      steps: [
        "Read the brief, restate it back in plain words to catch ambiguity early.",
        "Split into small reversible steps. Each step ships behind a verifiable check.",
        "Wire the boring parts first (data flow, error states, deploy) before polishing UI.",
        "Code in small atomic commits. Each commit has a why in the subject.",
        "Verify by running the thing, not just by trusting the type checker.",
      ],
    },
    ai: {
      heading: "AI in my workflow",
      items: [
        "Claude Code / Cursor as a co-developer for boilerplate, scaffolds, and code review on diffs.",
        "OpenAI / Anthropic API in product features (summaries, classifiers, tool calling).",
        "I treat AI output as a draft - read every line, fix what is wrong, never trust it on edge cases.",
        "I write the architecture and intent myself, then let AI fill in the typing work.",
      ],
    },
  },
  build: {
    title: "How I built this site",
    intro: "Direct answers to the four evaluation questions from the brief, grounded in actual choices made for this project.",
    items: [
      {
        heading: "How I write frontend",
        body: "Vanilla TypeScript + Vite + SCSS, no framework. The surface is small so rendering is plain DOM string templates re-mounted on state change instead of a vDOM. CSS variables drive theming; one `data-theme` attribute on the root flips dark / light. All UI copy lives in `src/i18n.ts` so adding a locale is a copy and translate, not a refactor. Strict tsconfig (no `any`, no unused), every interpolation is escaped against XSS, focus rings respected, `prefers-reduced-motion` honoured.",
      },
      {
        heading: "How I work with API",
        body: "Hono on Node behind Caddy, one repo and one deploy. Every request is validated with zod via `@hono/zod-validator`; on failure the validator returns a structured `{ ok: false, error }` JSON so the client renders the exact reason. The contact handler runs the AI summary call and the two Resend sends with explicit timeouts; the AI call degrades silently on failure so the email always goes out. Loading / success / error are explicit states in the client, the submit button disables and the inputs lock during the request.",
      },
      {
        heading: "How I use AI tools",
        body: "In development: Claude Code and Cursor scaffold boilerplate, generate first drafts of types and tests, and review diffs. Every generated line is re-read and adjusted; I never trust AI on edge cases or anything security-related. In production: an OpenAI-compatible client points at Groq (`llama-3.1-8b-instant`) for the per-submission summary; switching providers is one environment variable. AI is a nice-to-have, never a blocker.",
      },
      {
        heading: "How I organize the project",
        body: "`src/` for frontend, `server/` for backend, `public/` for static assets (`robots.txt`, `sitemap.xml`, `llms.txt`), `docs/` for the original PRD. Three tsconfigs (shared root, frontend, server) keep the type surfaces isolated. Multi-stage Dockerfile with separate build and runtime images, single docker-compose service, healthcheck wired to `/health`. `.env.example` is committed, `.env` is git-ignored. Atomic commits with a `why` in the subject. Lighthouse was 100 / 100 / 100 / 100 before the last push.",
      },
    ],
  },
  cases: {
    title: "Cases",
    items: [
      {
        heading: "vega.selim.services",
        sub: "Chat interface with AI (test task for another company)",
        body: "Streaming chat UI on Nuxt + custom backend. Token streaming, message threading, error recovery.",
        links: [
          { label: "Live", href: "https://vega.selim.services" },
          { label: "Repo", href: "https://github.com/selimdev00/vega-chat" },
        ],
      },
      {
        heading: "fmf.selim.services",
        sub: "Frontend test task (another company)",
        body: "Static landing assembled from a Figma mock. Same deploy approach as this project: single repo, own Hetzner VPS, subdomain under selim.services.",
        links: [
          { label: "Live", href: "https://fmf.selim.services" },
          { label: "Repo", href: "https://github.com/selimdev00/lyra" },
        ],
      },
      {
        heading: "selim.services",
        sub: "My personal portfolio (Nuxt 3)",
        body: "The structure of this test landing is close to my own portfolio. The repo is old and not linked here on purpose; the live site is the source of truth.",
        links: [{ label: "Live", href: "https://selim.services" }],
      },
      {
        heading: "This site (rigel)",
        sub: "The page you are reading now",
        body: "TypeScript + Vite + SCSS on the frontend, Node + Hono on the backend, Resend for email, OpenAI for the AI summary of incoming submissions. One repo, single deploy on Hetzner.",
      },
    ],
  },
  contacts: {
    title: "Contacts",
    links: [
      { label: "selimdev00@gmail.com", href: "mailto:selimdev00@gmail.com" },
      { label: "Telegram @selimdevv", href: "https://t.me/selimdevv" },
      { label: "GitHub", href: "https://github.com/selimdev00" },
      { label: "Portfolio", href: "https://selim.services" },
    ],
    form: {
      title: "Send a message",
      sub: "Goes to my inbox; a copy is sent back to you. Comments are summarized by an AI before being attached to the owner email.",
      labels: { name: "Name", phone: "Phone", email: "Email", comment: "Comment" },
      send: "Send",
      sending: "Sending...",
      successTitle: "Sent.",
      successBody: "A copy is on its way to your inbox.",
      summaryPrefix: "AI summary attached to the owner email:",
      errorTitle: "Could not send.",
      errorFallback: "Try again in a moment.",
    },
  },
  validation: {
    required: "Required",
    nameMin: "At least 2 characters",
    phoneInvalid: "Looks invalid",
    phoneExpectDigits: "Expected {expected} digits for {country}, got {got}",
    phoneWrongCode: "Must start with {dial} for {country}",
    emailMissingAt: "Missing @",
    emailMissingDomain: "Missing domain after @",
    emailInvalid: "Invalid email format",
    commentMin: "At least 5 characters",
  },
  footer: {
    text: "Built for the LabX test task, May 2026.",
    sourceLabel: "Source on GitHub",
    repoHref: "https://github.com/selimdev00/rigel",
  },
  chat: {
    open: "Ask the site",
    close: "Close chat",
    title: "Ask anything about Selim",
    sub: "Stack, experience, cases, contacts. Grounded in profile data via tool calling.",
    greeting: "Hi. Ask me about Selim's stack, experience, cases, how to contact him, or what this site is built with.",
    placeholder: "Type a question...",
    send: "Send",
    thinking: "Thinking...",
    toolsUsedPrefix: "tools called:",
    toolNote: "OpenAI-compatible (Groq llama-3.3-70b-versatile) with 6 read-only tools. No invented facts.",
    examples: ["What's Selim's stack?", "How can I contact him?", "What is this site built with?"],
    hint: "Here is the AI",
  },
};

const ru: Dict = {
  htmlLang: "ru",
  meta: {
    title: "Селим Атабаллыев - фронтенд / фуллстек разработчик",
    description: "Мидл фронтенд / фуллстек инженер. TypeScript, Nuxt, Next, Node, AI-интеграции.",
  },
  ui: {
    themeToggleLight: "Светлая тема",
    themeToggleDark: "Тёмная тема",
    langToggle: "EN",
    langSwitchLabel: "Переключить на English",
    skipToContent: "К содержимому",
    menu: "Открыть меню",
    close: "Закрыть",
  },
  nav: { about: "О себе", process: "Как работаю", build: "Стек проекта", cases: "Кейсы", contacts: "Контакты" },
  hero: {
    eyebrow: "Фронтенд / Фуллстек разработчик",
    name: "Селим Атабаллыев",
    tagline: "Программирую с 14, коммерчески с 2020. TypeScript-first. Фронтенд, Node-бэкенды, AI-интеграции.",
  },
  about: {
    title: "О себе",
    stack: {
      heading: "Стек",
      items: ["TypeScript", "Nuxt 3", "Next.js", "Vue 3", "React", "Node.js", "Hono", "NestJS", "SCSS / Tailwind", "PostgreSQL", "Docker", "Strapi / WordPress"],
    },
    experience: {
      heading: "Опыт",
      body: "6 лет в коммерческой разработке. Продакшен веб-приложения, браузерные расширения, CRM, AI-интеграции, видео-пайплайны. Менторил 25+ разработчиков в разных командах.",
    },
    directions: {
      heading: "Направления",
      items: [
        "Фронтенд SPA и лендинги",
        "SSR / статика (Nuxt, Next, Astro)",
        "Node API (Hono, Nest, Express)",
        "Браузерные расширения (MV3, мульти-браузер)",
        "AI-интеграции (OpenAI, Anthropic, tool calling)",
      ],
    },
  },
  process: {
    title: "Как я работаю",
    approach: {
      heading: "Подход к задачам",
      steps: [
        "Читаю бриф и пересказываю его своими словами, чтобы поймать неоднозначности на старте.",
        "Дроблю на маленькие обратимые шаги. Каждый шаг проходит через проверку перед мерджем.",
        "Сначала пробрасываю скучные части (поток данных, ошибки, деплой), потом полирую UI.",
        "Атомарные коммиты. В заголовке - зачем, не что.",
        "Проверяю не тайпчекером, а запуском - руками или скриптом.",
      ],
    },
    ai: {
      heading: "AI в работе",
      items: [
        "Claude Code / Cursor как ко-разработчик для бойлерплейта, скаффолдов и ревью диффов.",
        "OpenAI / Anthropic API в продуктовых фичах (суммаризация, классификация, tool calling).",
        "Любой AI-вывод - это черновик. Читаю каждую строчку, чиню что не так, на edge cases не доверяю.",
        "Архитектуру и намерение пишу сам, AI добивает рутину по типизации.",
      ],
    },
  },
  build: {
    title: "Как сделан этот сайт",
    intro: "Прямые ответы на четыре вопроса оценки из тестового, привязанные к реальным решениям этого проекта.",
    items: [
      {
        heading: "Как я пишу frontend",
        body: "Чистый TypeScript + Vite + SCSS, без фреймворка. Поверхность маленькая, поэтому рендер - обычные DOM-шаблоны, перемонтируемые на изменение состояния, без vDOM. CSS-переменные держат тему; один `data-theme` на корне переключает dark / light. Вся UI-текстовка живёт в `src/i18n.ts` - добавить локаль = скопировать и перевести, без рефакторинга. Строгий tsconfig (никаких `any`, никаких unused), каждая интерполяция экранируется против XSS, focus-rings на месте, `prefers-reduced-motion` уважается.",
      },
      {
        heading: "Как я работаю с API",
        body: "Hono на Node за Caddy, один репозиторий и один деплой. Каждый запрос валидируется zod через `@hono/zod-validator`; при ошибке валидатор возвращает структурированный `{ ok: false, error }` JSON - клиент показывает точную причину. Обработчик контакта запускает AI-суммаризацию и две отправки Resend с явными таймаутами; AI-вызов тихо падает при ошибке, чтобы письмо ушло в любом случае. Loading / success / error - явные состояния в клиенте, кнопка submit блокируется и поля закрываются на время запроса.",
      },
      {
        heading: "Как я использую AI-инструменты",
        body: "В разработке: Claude Code и Cursor скаффолдят boilerplate, пишут первые черновики типов и тестов, ревьюят диффы. Каждая сгенерированная строчка перечитана и поправлена; на edge cases или security я AI не доверяю. В продакшне: OpenAI-совместимый клиент смотрит в Groq (`llama-3.1-8b-instant`) для суммаризации каждой заявки; смена провайдера - одна переменная окружения. AI - приятный бонус, не блокер.",
      },
      {
        heading: "Как я организую проект",
        body: "`src/` - фронт, `server/` - бэк, `public/` - статика (`robots.txt`, `sitemap.xml`, `llms.txt`), `docs/` - оригинальный PRD. Три tsconfig (общий корневой, фронт, сервер) изолируют поверхности типов. Многоступенчатый Dockerfile с раздельными build и runtime образами, один сервис в docker-compose, healthcheck на `/health`. `.env.example` в git, `.env` - игнор. Атомарные коммиты с `why` в заголовке. Lighthouse был 100 / 100 / 100 / 100 перед последним пушем.",
      },
    ],
  },
  cases: {
    title: "Кейсы",
    items: [
      {
        heading: "vega.selim.services",
        sub: "Чат-интерфейс с AI (тестовое для другой компании)",
        body: "Стриминговый чат на Nuxt + кастомный бэкенд. Токен-стриминг, треды сообщений, восстановление после ошибок.",
        links: [
          { label: "Live", href: "https://vega.selim.services" },
          { label: "Репозиторий", href: "https://github.com/selimdev00/vega-chat" },
        ],
      },
      {
        heading: "fmf.selim.services",
        sub: "Фронтенд тестовое (другая компания)",
        body: "Статический лендинг, собранный из Figma-макета. Тот же подход к деплою что и у этого проекта: один репозиторий, свой Hetzner VPS, субдомен под selim.services.",
        links: [
          { label: "Live", href: "https://fmf.selim.services" },
          { label: "Репозиторий", href: "https://github.com/selimdev00/lyra" },
        ],
      },
      {
        heading: "selim.services",
        sub: "Моё личное портфолио (Nuxt 3)",
        body: "Структура этого тестового лендинга близка к моему портфолио. Репозиторий старый, поэтому ссылку специально не прикладываю; источник правды - сам сайт.",
        links: [{ label: "Live", href: "https://selim.services" }],
      },
      {
        heading: "Этот сайт (rigel)",
        sub: "Страница, которую вы сейчас читаете",
        body: "TypeScript + Vite + SCSS на фронте, Node + Hono на бэке, Resend для писем, OpenAI для AI-суммаризации входящих сообщений. Один репозиторий, один деплой на Hetzner.",
      },
    ],
  },
  contacts: {
    title: "Контакты",
    links: [
      { label: "selimdev00@gmail.com", href: "mailto:selimdev00@gmail.com" },
      { label: "Telegram @selimdevv", href: "https://t.me/selimdevv" },
      { label: "GitHub", href: "https://github.com/selimdev00" },
      { label: "Портфолио", href: "https://selim.services" },
    ],
    form: {
      title: "Написать сообщение",
      sub: "Письмо приходит мне на почту; копия уходит вам. Перед отправкой комментарий проходит через AI-суммаризацию и прикладывается к письму владельцу.",
      labels: { name: "Имя", phone: "Телефон", email: "Email", comment: "Сообщение" },
      send: "Отправить",
      sending: "Отправка...",
      successTitle: "Отправлено.",
      successBody: "Копия письма уже идёт вам на почту.",
      summaryPrefix: "AI-резюме, приложенное к письму владельцу:",
      errorTitle: "Не удалось отправить.",
      errorFallback: "Попробуйте ещё раз через минуту.",
    },
  },
  validation: {
    required: "Обязательно",
    nameMin: "Минимум 2 символа",
    phoneInvalid: "Похоже на ошибку",
    phoneExpectDigits: "Ожидается {expected} цифр для {country}, введено {got}",
    phoneWrongCode: "Должен начинаться с {dial} для {country}",
    emailMissingAt: "Не хватает @",
    emailMissingDomain: "Не хватает домена после @",
    emailInvalid: "Некорректный формат email",
    commentMin: "Минимум 5 символов",
  },
  footer: {
    text: "Сделано для тестового задания LabX, май 2026.",
    sourceLabel: "Исходники на GitHub",
    repoHref: "https://github.com/selimdev00/rigel",
  },
  chat: {
    open: "Спросить сайт",
    close: "Закрыть чат",
    title: "Спросите про Селима",
    sub: "Стек, опыт, кейсы, контакты. Ответы только из данных профиля через tool calling.",
    greeting: "Привет. Спросите про стек Селима, опыт, кейсы, как с ним связаться или на чём сделан этот сайт.",
    placeholder: "Введите вопрос...",
    send: "Отправить",
    thinking: "Думаю...",
    toolsUsedPrefix: "вызвано инструментов:",
    toolNote: "OpenAI-совместимый (Groq llama-3.3-70b-versatile) + 6 read-only инструментов. Без выдуманных фактов.",
    examples: ["Какой стек у Селима?", "Как с ним связаться?", "На чём сделан этот сайт?"],
    hint: "Здесь AI-интеграция",
  },
};

const DICTS: Record<Locale, Dict> = { en, ru };
const LOCALE_KEY = "rigel.locale";

export function detectLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_KEY);
  if (stored === "en" || stored === "ru") return stored;
  const nav = navigator.language?.toLowerCase() || "";
  if (nav.startsWith("ru")) return "ru";
  return "en";
}

export function persistLocale(l: Locale) {
  localStorage.setItem(LOCALE_KEY, l);
}

export function getDict(l: Locale): Dict {
  return DICTS[l];
}

export function otherLocale(l: Locale): Locale {
  return l === "en" ? "ru" : "en";
}
